/**
 * ConversationThread
 *
 * Chat interface for a single conversation.
 * Composes MessageList, ChatInput, ReplyBar, and ScrollToBottomButton.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Avatar, LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/hooks';
import { useSchool } from '@/contexts/SchoolContext';
import useConversationMessages from '../hooks/useConversationMessages';
import useMessengerStore from '@/stores/messengerStore';
import MediaUploadService from '@/services/messaging/MediaUploadService';
import ConversationService from '@/services/messaging/ConversationService';
import { MESSAGE_TYPES } from '@/models/Conversation';
import { ArrowLeftIcon, InformationCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import ReplyBar from './ReplyBar';
import ScrollToBottomButton from './ScrollToBottomButton';
import TypingIndicator from './TypingIndicator';
import SearchInConversation from './SearchInConversation';
import ForwardModal from './ForwardModal';
import useTypingIndicator from '../hooks/useTypingIndicator';

export default function ConversationThread({ conversation, conversations = [], onBack, onToggleInfo, panelMode = false }) {
  const { user } = useAuth();
  const { schoolId } = useSchool();
  const [messageText, setMessageText] = useState('');
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [forwardingMessage, setForwardingMessage] = useState(null);
  const [draftSending, setDraftSending] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null); // { id, type, fileName, progress }
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const { replyingTo, setReplyingTo, clearReplyingTo, selectConversation } = useMessengerStore();

  const isDraft = conversation?._draft && !conversation?.id;

  const {
    messages,
    loading,
    error,
    sending,
    hasMore,
    sendMessage,
    editMessage,
    deleteForMe,
    deleteForEveryone,
    loadMore
  } = useConversationMessages(isDraft ? null : conversation?.id, {
    limit: 50,
    autoMarkAsRead: true
  });

  const { onKeyStroke, clearTyping } = useTypingIndicator(isDraft ? null : conversation?.id);

  // Auto-scroll to bottom on new messages (only if near bottom)
  useEffect(() => {
    if (messagesEndRef.current && !showScrollButton) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // Mobile keyboard: scroll to bottom when virtual keyboard opens
  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };
    window.visualViewport?.addEventListener('resize', handleResize);
    return () => window.visualViewport?.removeEventListener('resize', handleResize);
  }, []);

  // Track scroll position
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom > 200);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Send message (handles draft conversations — creates on first send)
  const handleSend = async () => {
    if (!messageText.trim() || sending || draftSending) return;

    // Draft conversation: create it first, then send the message
    if (isDraft) {
      setDraftSending(true);
      try {
        const conversationId = await ConversationService.createConversation({
          schoolId: conversation.schoolId || schoolId,
          type: conversation.type,
          subject: conversation.subject,
          participants: conversation.participants,
          createdBy: user.uid,
          initialMessage: messageText.trim()
        });

        // Switch to the real conversation
        selectConversation({
          ...conversation,
          id: conversationId,
          _draft: false
        });
        setMessageText('');
      } catch (err) {
        console.error('Error creating conversation:', err);
      } finally {
        setDraftSending(false);
      }
      return;
    }

    const replyData = replyingTo ? {
      messageId: replyingTo.id,
      senderId: replyingTo.senderId,
      senderName: replyingTo.senderName,
      text: replyingTo.text?.substring(0, 100),
      type: replyingTo.type
    } : null;

    try {
      await sendMessage(messageText, replyData);
      setMessageText('');
      clearReplyingTo();
      clearTyping();
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  // Send media message (image or file)
  const handleSendMedia = async (file, type) => {
    if (!conversation?.id || !user?.uid) return;

    const uploadId = `upload-${Date.now()}`;
    setPendingUpload({ id: uploadId, type, fileName: file.name, progress: 0 });

    try {
      const media = await MediaUploadService.uploadFile(conversation.id, file, (progress) => {
        setPendingUpload(prev => prev?.id === uploadId ? { ...prev, progress } : prev);
      });
      const messageType = type === 'image' ? MESSAGE_TYPES.IMAGE : MESSAGE_TYPES.FILE;

      await ConversationService.sendMessage(conversation.id, {
        senderId: user.uid,
        senderName: user.displayName || user.email || 'Unknown',
        senderRole: 'teacher',
        text: '',
        media,
        type: messageType
      });
    } catch (err) {
      console.error('Error sending media:', err);
      alert(err.message || 'Failed to upload file');
    } finally {
      setPendingUpload(null);
    }
  };

  // Send voice note
  const handleSendVoice = async (audioBlob, duration) => {
    if (!conversation?.id || !user?.uid) return;

    try {
      const media = await MediaUploadService.uploadVoiceNote(conversation.id, audioBlob, duration);

      await ConversationService.sendMessage(conversation.id, {
        senderId: user.uid,
        senderName: user.displayName || user.email || 'Unknown',
        senderRole: 'teacher',
        text: '',
        media,
        type: MESSAGE_TYPES.VOICE
      });
    } catch (err) {
      console.error('Error sending voice note:', err);
    }
  };

  // Edit handlers
  const handleStartEdit = (message) => {
    setActiveMessageId(null);
    setEditingMessage(message);
    setEditText(message.text);
  };

  const handleSaveEdit = async () => {
    if (!editingMessage || !editText.trim()) return;
    try {
      await editMessage(editingMessage.id, editText.trim());
      setEditingMessage(null);
      setEditText('');
    } catch (err) {
      console.error('Error editing message:', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  // Search highlight: scroll to message
  const handleHighlightMessage = useCallback((messageId) => {
    setHighlightedMessageId(messageId);
    const el = document.getElementById(`msg-${messageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Flash highlight
      el.classList.add('bg-warning/20');
      setTimeout(() => el.classList.remove('bg-warning/20'), 2000);
    }
  }, []);

  // No conversation selected
  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center text-base-content/50">
        <div className="text-center">
          <svg className="w-20 h-20 mx-auto mb-4 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-lg">Select a conversation</p>
          <p className="text-sm mt-1">Choose from the inbox or start a new one</p>
        </div>
      </div>
    );
  }

  // Other participants
  const otherParticipants = conversation.participants?.filter(
    p => p.userId !== user?.uid
  ) || [];

  const displayName = conversation.type === 'direct' && otherParticipants.length === 1
    ? otherParticipants[0].name
    : conversation.subject;

  return (
    <div className="h-full flex flex-col bg-base-100">
      {/* Header */}
      <div className="border-b border-base-300 bg-base-100 px-4 py-3 flex items-center gap-3">
        {onBack && (
          <button className={`btn btn-ghost btn-sm btn-circle ${panelMode ? '' : 'lg:hidden'}`} onClick={onBack}>
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
        )}

        <div className="flex-shrink-0">
          {otherParticipants.length === 1 ? (
            <Avatar name={otherParticipants[0].name} userId={otherParticipants[0].userId} photoURL={otherParticipants[0].photoURL} size="sm" />
          ) : (
            <div className="avatar-group -space-x-3">
              {otherParticipants.slice(0, 3).map(p => (
                <Avatar key={p.userId} name={p.name} userId={p.userId} photoURL={p.photoURL} size="sm" />
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm truncate">{displayName}</h3>
          <p className="text-xs text-base-content/60 truncate">
            {conversation.type === 'direct'
              ? otherParticipants[0]?.name || ''
              : `${otherParticipants.map(p => p.name).join(', ')} · ${conversation.participants?.length} members`
            }
          </p>
        </div>

        <button
          className="btn btn-ghost btn-sm btn-circle"
          onClick={() => setShowSearch(!showSearch)}
          title="Search in conversation"
        >
          <MagnifyingGlassIcon className="w-5 h-5" />
        </button>

        {onToggleInfo && (
          <button className="btn btn-ghost btn-sm btn-circle hidden lg:flex" onClick={onToggleInfo}>
            <InformationCircleIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Search bar */}
      {showSearch && (
        <SearchInConversation
          messages={messages}
          onHighlight={handleHighlightMessage}
          onClose={() => { setShowSearch(false); setHighlightedMessageId(null); }}
        />
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-2 relative"
        onScroll={handleScroll}
      >
        {hasMore && (
          <div className="text-center py-2">
            <button className="btn btn-ghost btn-xs" onClick={() => loadMore()}>
              Load older messages
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="alert alert-error max-w-md mx-auto mt-8">
            <span>{error}</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-base-content/50 py-12">
            <p>No messages yet. Say hello!</p>
          </div>
        ) : (
          <MessageList
            messages={messages}
            currentUserId={user?.uid}
            isGroup={conversation.type === 'group' || conversation.participants?.length > 2}
            onReply={(msg) => setReplyingTo(msg)}
            onForward={(msg) => setForwardingMessage({ ...msg, conversationId: conversation.id })}
            onEdit={handleStartEdit}
            onDeleteForMe={deleteForMe}
            onDeleteForEveryone={deleteForEveryone}
            onAddReaction={async (messageId, emoji) => {
              if (!conversation?.id || !user?.uid) return;
              try {
                const msg = messages.find(m => m.id === messageId);
                if (msg?.senderId === user.uid) return; // can't react to own messages
                if (msg?.reactions?.[emoji]?.includes(user.uid)) return; // already reacted
                const { default: svc } = await import('@/services/messaging/ConversationMessageService');
                await svc.addReaction(conversation.id, messageId, emoji, user.uid);
                // Notify message sender (not yourself)
                if (msg?.senderId && msg.senderId !== user.uid && schoolId) {
                  try {
                    const { default: notifSvc } = await import('@/features/notifications/services/NotificationService');
                    await notifSvc.createNotification({
                      type: 'reaction',
                      priority: 'low',
                      category: 'messaging',
                      title: `${emoji} Reaction`,
                      message: `${user.displayName || 'Someone'} reacted ${emoji} to your message`,
                      metadata: {
                        conversationId: conversation.id,
                        messageId,
                        emoji,
                        reactorId: user.uid,
                        reactorName: user.displayName || user.email
                      },
                      schoolId,
                      recipientIds: [msg.senderId],
                      createdById: user.uid,
                      actionable: true,
                      actionUrl: `/messages`,
                      actionLabel: 'View message',
                      expiresInDays: 7
                    });
                  } catch (err) {
                    console.error('Failed to create reaction notification:', err);
                  }
                }
              } catch (err) {
                console.error('Error adding reaction:', err);
              }
            }}
            onRemoveReaction={async (messageId, emoji) => {
              if (!conversation?.id || !user?.uid) return;
              try {
                const msg = messages.find(m => m.id === messageId);
                if (!msg?.reactions?.[emoji]?.includes(user.uid)) return; // haven't reacted
                const { default: svc } = await import('@/services/messaging/ConversationMessageService');
                await svc.removeReaction(conversation.id, messageId, emoji, user.uid);
              } catch (err) {
                console.error('Error removing reaction:', err);
              }
            }}
            editingMessage={editingMessage}
            editText={editText}
            setEditText={setEditText}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            activeMessageId={activeMessageId}
            setActiveMessageId={setActiveMessageId}
          />
        )}

        {/* Upload in progress — shows as a bubble with progress bar */}
        {pendingUpload && (
          <div className="flex justify-end mt-3">
            <div className="px-3 py-2 rounded-xl bg-primary text-primary-content rounded-br-sm max-w-[75%]">
              <div className="text-sm opacity-70 truncate mb-1">
                {pendingUpload.type === 'image' ? '📷' : '📎'} {pendingUpload.fileName}
              </div>
              <div className="w-full bg-primary-content/20 rounded-full h-1.5">
                <div
                  className="bg-primary-content/70 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${pendingUpload.progress}%` }}
                />
              </div>
              <div className="text-[10px] opacity-50 mt-1">{pendingUpload.progress}%</div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom */}
      {showScrollButton && (
        <ScrollToBottomButton onClick={scrollToBottom} />
      )}

      {/* Typing indicator */}
      <TypingIndicator
        typing={conversation?.typing}
        currentUserId={user?.uid}
        participants={conversation?.participants}
      />

      {/* Reply bar */}
      <ReplyBar replyingTo={replyingTo} onCancel={clearReplyingTo} />

      {/* Input */}
      <ChatInput
        value={messageText}
        onChange={(val) => { setMessageText(val); onKeyStroke(); }}
        onSend={handleSend}
        onSendMedia={handleSendMedia}
        onSendVoice={handleSendVoice}
        sending={sending}
      />

      {/* Forward modal */}
      <ForwardModal
        open={!!forwardingMessage}
        onClose={() => setForwardingMessage(null)}
        message={forwardingMessage}
        conversations={conversations}
      />
    </div>
  );
}
